import { DateTime } from 'luxon';
import { getLocationName, getSeatRowName } from '../../components/flight/amenities/SeatInformation';
import { capitalize } from '../../components/flight/helpers/capitalize';
import {
  DefaultAccommodationCancellationPolicyFragment,
  DefaultFlightOfferFragment,
  DefaultFlightTravelerSelectionFragment,
} from '../../graphql/generated/graphql';
import { formatLocalDateWithoutYear } from '../../helpers/formatTime';

// Helpers used by the booking PDF — these mirror the email-template helpers in
// apps/backend/src/user-templates so the PDF renders the same lines the
// confirmation email does (the email is the canonical visual reference).

// Stopover duration between two consecutive segments (uses `setZone` so the
// time math respects each endpoint's local timezone rather than the browser's).
export function getStopoverDurationBetweenSegments(
  previous: DefaultFlightOfferFragment['itineraries'][0]['segments'][0],
  next: DefaultFlightOfferFragment['itineraries'][0]['segments'][0],
): string {
  const arrival = previous.arrival?.datetime;
  const departure = next.departure?.datetime;
  if (!arrival || !departure) {
    return '';
  }
  const arrivalDt = DateTime.fromISO(arrival, { setZone: true });
  const departureDt = DateTime.fromISO(departure, { setZone: true });
  if (!arrivalDt.isValid || !departureDt.isValid) {
    return '';
  }
  return departureDt.diff(arrivalDt, ['hours', 'minutes']).toFormat("h'h' m'm'");
}

// Format a single seat for the footer ("12A (Aisle, Front)").
function formatSeat(
  seat: NonNullable<NonNullable<DefaultFlightTravelerSelectionFragment['seats']>[number]['seat']>,
): string {
  const row = getSeatRowName(seat);
  const location = getLocationName(seat);
  const meta = [row, location].filter(Boolean).map((s) => capitalize(s.toLowerCase()));
  if (meta.length === 0) {
    return seat.designator;
  }
  return `${seat.designator} (${meta.join(', ')})`;
}

// Comma-joined seat list across all segments of an itinerary. Empty when no
// seats are selected (matches the email's getSeatSummary).
export function getSeatSummary(
  itinerary: DefaultFlightOfferFragment['itineraries'][0],
  travelerSelections: DefaultFlightTravelerSelectionFragment[] | undefined,
): string {
  const selected = itinerary.segments
    .map((segment) => travelerSelections?.[0]?.seats.find((s) => s.segmentId === segment.id)?.seat)
    .filter((seat): seat is NonNullable<typeof seat> => Boolean(seat));
  return selected.map(formatSeat).join(', ');
}

// Baggage line — mirrors getBaggageAllowanceText from backend.
export function getBaggageAllowanceText(
  baggage: NonNullable<DefaultFlightOfferFragment['itineraries'][0]['fares'][0]['baggage']> | undefined | null,
): string {
  if (!baggage) {
    return 'Information not specified';
  }
  const parts: string[] = [];
  if (baggage.checked?.included && baggage.checked.total > 0) {
    parts.push(
      baggage.checked.weight
        ? `${baggage.checked.total} x ${baggage.checked.weight}kg Checked`
        : `${baggage.checked.total} x Checked`,
    );
  }
  if (baggage.cabin?.included && baggage.cabin.total > 0) {
    parts.push(
      baggage.cabin.weight
        ? `${baggage.cabin.total} x ${baggage.cabin.weight}kg Carry-on`
        : `${baggage.cabin.total} x Carry-on`,
    );
  }
  return parts.length > 0 ? parts.join(' + ') : 'Information not specified';
}

// Refundable / non-refundable line for the hotel card. Mirrors the email's
// cancellationLine helper so wording stays consistent across channels.
export function cancellationLine(cancellationPolicies: DefaultAccommodationCancellationPolicyFragment[]): {
  refundable: boolean;
  text: string;
} {
  const refundPolicy = cancellationPolicies.find((policy) => policy.type === 'refund');
  if (cancellationPolicies.length === 0 || !refundPolicy) {
    return { refundable: false, text: 'Non Refundable' };
  }
  const before = formatLocalDateWithoutYear(refundPolicy.before);
  if (refundPolicy.percentage === 100) {
    return { refundable: true, text: `Fully refundable before ${before}` };
  }
  return { refundable: true, text: `${Math.floor(refundPolicy.percentage)}% refund before ${before}` };
}

// Strip the tiny allowlist of HTML tags the supplier feeds (ul/li/b/i/p/br)
// down to plain text + paragraph breaks. We don't have a layout engine in
// pdf-lib so the renderer wraps each resulting line at the content width.
const TAG_RE = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi;
const ENTITY_MAP: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' ',
};
export function htmlToPlainTextLines(html: string | undefined | null): string[] {
  if (!html) {
    return [];
  }
  // Replace block-level closers and <br> with line breaks; list items get a bullet.
  let text = html
    .replace(/<\/(p|div|ul|ol)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<\/li>/gi, '\n');
  // Strip remaining tags.
  text = text.replace(TAG_RE, '');
  // Decode the handful of entities supplier text actually uses.
  text = text.replace(/&[a-z#0-9]+;/gi, (match) => ENTITY_MAP[match.toLowerCase()] ?? match);
  // Normalize whitespace per line and drop empties.
  return text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line) => line.length > 0);
}

// Build the same proxied image URL the SPA's ProxyImage uses so the renderer
// fetches the bytes via our backend (sidesteps supplier CORS) and gets a
// predictable Content-Type for the embed decision.
export function buildProxyImageUrl(backendUrl: string, rawUrl: string): { url: string; ext: 'jpg' | 'png' } {
  const match = rawUrl.match(/\.(png|jpe?g|webp|gif)(\?|$)/i);
  const rawExt = (match?.[1]?.toLowerCase() ?? 'jpg').replace('jpeg', 'jpg');
  // pdf-lib only embeds jpg and png — route gifs/webps through the proxy as jpg
  // since the proxy transcodes by extension hint.
  const ext: 'jpg' | 'png' = rawExt === 'png' ? 'png' : 'jpg';
  const base64 = btoa(rawUrl).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  return { url: `${backendUrl}/proxy/image.${ext}/${base64}`, ext };
}
